import { CursorPageQuery } from 'types/definitions'

export interface CouponDTO {
  id: string
  code: string
  workspace_id: string
  friendly_name?: string
  description?: string
  max_total_redemptions: number
  date_expires?: string
  duration: CouponDuration
  coupon_type: CouponTypeDTO
  date_created: string
  date_modified: string
  off_type: OffTypeDTO
  max_off?: MaxOffDTO
}

/**
 * How many times can a single user redeem this coupon?
 * Note that a single user can at any time only have
 * a single unredeemed instance of the coupon at any time,
 * regardless of this value.
 */
export type CouponDuration = 'ONCE' | 'FOREVER'
/**
 * A Coupon that provides a fixed discount.
 */
export interface AmountOffTypeDTO {
  type: 'AMOUNT_OFF'
  amount_off: number
}
/**
 * A Coupon that provides a percent-based discount.
 */
export interface PercentOffTypeDTO {
  type: 'PERCENT_OFF'
  percent_off: number
}
/**
 * The maximum amount discount on PercentType coupon
 */
export interface MaxAmountOffTypeDTO {
  type: 'MAX_AMOUNT_OFF'
  amount_off: number
}
/**
 * The maximum percent off discount on AmountType coupon
 */
export interface MaxPercentOffTypeDTO {
  type: 'MAX_PERCENT_OFF'
  percent_off: number
}
/**
 * Off Type union.
 */
export declare type OffTypeDTO = AmountOffTypeDTO | PercentOffTypeDTO
export declare type MaxOffDTO = MaxAmountOffTypeDTO | MaxPercentOffTypeDTO
/**
 * The link between a user and a coupon.
 */
export interface UserCouponDTO {
  id: string
  user_id: string
  coupon: CouponDTO
  redeemed: boolean
  date_created: string
  date_modified: string
  date_redeemed?: string
}
export interface AddUserCouponPayload {
  user_public_id: string
  workspace_id: string
}
/**
 * Plain old coupon.
 */
export interface RegularCouponTypeDTO {
  type: 'REGULAR'
}
/**
 * In case we are feeling extra generous, we can add a custom discount
 * to specific users.
 */
export interface CustomDiscountCouponTypeDTO {
  type: 'CUSTOM_DISCOUNT'
  created_by_user_id: string
}
/**
 * Used by the platform API to set up referral coupons for users.
 */
export interface ReferralCouponTypeDTO {
  type: 'REFERRAL_CODE'
  referrer_user_id: string
}
/**
 * Coupon Type Enum.
 */
export declare type CouponTypeDTO =
  | RegularCouponTypeDTO
  | ReferralCouponTypeDTO
  | CustomDiscountCouponTypeDTO

export enum CouponStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  ALL = 'ALL',
  REDEEMED = 'REDEEMED',
}

/**
 * Possible values to Query Coupons by.
 */
export interface FindCouponQuery extends Partial<CursorPageQuery> {
  code?: string
  workspace_id: string
  duration?: CouponDuration
  coupon_type?: CouponDTO['coupon_type']['type']
  off_type?: CouponDTO['off_type']['type']
  status?: CouponStatus
  search_text?: string
}

/**
 * Shared properties to create a coupon
 */
export interface CreateCouponPayload {
  workspace_id: string
  code: string
  friendly_name?: string
  description?: string
  max_total_redemptions: number
  date_expires?: string
  duration: CouponDuration
  coupon_type: CouponTypeDTO
  off_type: OffTypeDTO
  max_off: MaxOffDTO | null
}
/**
 * Properties to update a coupon. Same as for creating, but all fields are optional.
 */
export declare type UpdateCouponPayload = Partial<CreateCouponPayload>
